import { Box, Checkbox, FormControlLabel } from '@mui/material'
import { useGetAllEventChangeRequests, useGetAllEvents, useGetEventConfig } from 'api/events/events.api'
import { allowsPartialTagMatching } from 'helpers/filterComponent.helper/filterComponent.helper'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import { useNavigate, useParams } from 'react-router'
import { removeFilterFromUrl } from 'store/filter/urlFilter.helper'
import globalState from 'store/global/global'
import { colors } from 'styles/variables'
import { Button, LoadingPlaceholderContainer } from 'ui/atoms'
import Card from 'ui/atoms/Card/Card'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import FilterComponent from 'ui/components/Filter/FilterComponent'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import ToggleMenu from 'ui/components/ToggleMenu/ToggleMenu'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import Tour from 'ui/components/Tour/Tour'
import ResetFilters from 'ui/molecules/ResetFilters/ResetFilters'
import { ToggleButton } from 'ui/molecules/ToggleButton/ToggleButton'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import ChangeRequestMenuItem from './components/ChangeRequestMenuItem/ChangeRequestMenuItem'
import EventPostModal from './components/EventPostModal/EventPostModal'
import EventPostViewModal from './components/EventPostViewModal/EventPostViewModal'
import EventsCalendar from './components/EventsCalendar/EventsCalendar'
import EventsTable from './components/EventsTable/EventsTable'
import EventsTour from './components/EventsTour/EventsTour'
import EventsViewHeader from './components/EventsViewHeader/EventsViewHeader'
import styles from './EventsView.module.less'

const FILTER_BUTTON_HEIGHT = 40
const FILTER_BUTTON_WIDTH = 100

const widgetConstants = {
  EVENTS_WIDGET_1: `item-1`,
  EVENTS_WIDGET_2: `item-2`,
  EVENTS_WIDGET_3: `item-3`,
  EVENTS_WIDGET_4: `item-4`,
  EVENTS_WIDGET_5: `item-5`,
}

export default function EventsView(): ReactElement {
  const { t } = useTranslation()
  const { systemId, user, hasAccess } = useAuth()

  const eventConfig = useGetEventConfig(systemId).data
  const allEventTagsData = eventConfig?.tags || []
  const navigate = useNavigate()
  const { eventId } = useParams<{ eventId?: string }>()
  const viewOrEdit = window.location.pathname.includes('edit') ? 'edit' : 'view'

  const CHANGE_REQUEST_PERMISSION = hasAccess({
    module: 'events',
    submodule: 'change_requests',
    permission: 'view_change_requests',
  })

  const eventsSteps: Array<Step> = useMemo(
    () => [
      {
        target: widgetConstants.EVENTS_WIDGET_1,
        content: <EventsTour />,
        title: `Welcome to the Event module`,
      },
      {
        target: widgetConstants.EVENTS_WIDGET_2,
        content: `Create new event - Content`,
        title: `Create a new event`,
      },
      {
        target: widgetConstants.EVENTS_WIDGET_3,
        content: `Events calendar - Content`,
        title: `Events - Calendar`,
      },
      {
        target: widgetConstants.EVENTS_WIDGET_4,
        content: `Events table - Content`,
        title: `Events - Table`,
      },
      {
        target: widgetConstants.EVENTS_WIDGET_5,
        content: `Filter events - Content`,
        title: `Filter events`,
      },
    ],
    []
  )

  const hasCreateOperationalEventsAccess = useHasPermission('create_operational_events')
  const globalSnap = useSnapshot(globalState)

  const [runTour, setRunTour] = useState<null | number>(null)
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false)
  const [openEventPostModal, setOpenEventPostModal] = useState(false)
  const [openSelectedEventPostViewModal, setOpenSelectedEventPostViewModal] = useState(false)
  const [filterIsCollapsed, setFilterCollapsed] = useState<boolean>(true)
  const [showOtherUsersDrafts, setShowOtherUsersDrafts] = useState<boolean>(false)

  const resetFilters = useCallback((): void => {
    removeFilterFromUrl(navigate)
  }, [navigate])

  const initDatePeriod = 'productionPlanPeriod'
  const startTimeProductionPlan = Datetime.getStartOfDay(Datetime.getISONow(0))
  const endTimeProductionPlan = Datetime.getEndOfDay(Datetime.getISONow(168))
  const [datePeriod, setDatePeriod] = useState<{ startTime: ISODateTime; endTime: ISODateTime }>({
    startTime: startTimeProductionPlan,
    endTime: endTimeProductionPlan,
  })

  const userId = user?.id

  const { data: eventsData, status: loadingEventsStatus } = useGetAllEvents(
    systemId,
    datePeriod.startTime,
    datePeriod.endTime
  )
  const events = useMemo(() => eventsData || [], [eventsData])

  const { data: pendingChangeRequests = [] } = useGetAllEventChangeRequests(
    systemId,
    'Pending',
    CHANGE_REQUEST_PERMISSION // only enbale if user have permission
  )

  const toggleFilterCollapse = (): void => {
    setFilterCollapsed(!filterIsCollapsed)
  }

  const eventTags = useMemo(
    () =>
      events.map((event) => {
        return event.tags ?? []
      }),
    [events]
  )
  const { activeTags, isActiveTagsFiltered } = useActiveTags({ availableFiltersSubset: eventTags })

  const filterTags = useMemo(() => (activeTags ? Array.from(activeTags) : []), [activeTags])

  const eventsVisibleToUser = events.filter((event) => {
    // Only hide a draft once we know who to compare it against. `userId` is undefined
    // until the signed-in user resolves, and a bare `!==` reads that as "someone else's",
    // which silently drops the operator's own drafts from both the table and the calendar
    // with nothing to indicate it (#4636).
    const isNotUsersDraft = userId !== undefined && event.status === 'Draft' && event.created_by_id !== userId
    return showOtherUsersDrafts || !isNotUsersDraft
  })

  const filteredEvents =
    filterTags.length === 0
      ? eventsVisibleToUser
      : eventsVisibleToUser.filter((event) => allowsPartialTagMatching(event.tags ?? [], filterTags))

  const setRunTourCallback = useCallback((state: boolean) => {
    setRunTour(state ? 0 : null)
  }, [])

  const setDatePeriodCallback = useCallback((startTime: ISODateTime, endTime: ISODateTime) => {
    setDatePeriod({ startTime, endTime })
  }, [])

  useEffect(() => {
    if (eventId) {
      if (viewOrEdit === 'view') {
        setOpenSelectedEventPostViewModal(true)
      } else {
        setOpenEventPostModal(true)
      }
    }
  }, [eventId, viewOrEdit])

  const resetPathName = useCallback(() => {
    const currentParams = location.search

    navigate({ pathname: '/events', search: currentParams }, { replace: true })
  }, [navigate])

  const pathNameId = eventId ? parseInt(eventId) : undefined

  const filterIcon = useMemo(
    () =>
      isActiveTagsFiltered ? (
        <Icon tooltip={{ title: t('Filters active'), placement: 'right' }} icon="fas fa-filter" />
      ) : (
        ''
      ),
    [t, isActiveTagsFiltered]
  )

  const MENU_WIDTH_IN_PERCENT = 15
  const CONTENT_WIDTH_IN_PERCENT = 100 - MENU_WIDTH_IN_PERCENT
  const CONTENT_WIDTH_MENU_OPEN_IN_PERCENT = CONTENT_WIDTH_IN_PERCENT - 2

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isChangeRequestFilterActive, setIsChangeRequestFilterActive] = useState(true)

  const handleMenuToggle = useCallback((isOpen: boolean): void => {
    setIsMenuOpen(isOpen)
  }, [])

  const toggleChangeRequestFilter = useCallback(() => {
    setIsChangeRequestFilterActive(!isChangeRequestFilterActive)
  }, [isChangeRequestFilterActive])

  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (headerRef.current) {
      //+150 to account for the padding of the header, otherwise the settings list will be cut off
      setHeaderHeight(headerRef.current.offsetHeight + 150)
    }
  }, [])

  return (
    <div className={widgetConstants.EVENTS_WIDGET_1}>
      <EventsViewHeader
        infoHeader={t('Events')}
        onInfoButtonClick={() => {
          setRunTour(0)
        }}
        secondaryChildren={
          <div style={{ display: filterIsCollapsed ? 'none' : 'block' }}>
            <FilterComponent availableFiltersSubset={eventTags} activeTags={activeTags} />
          </div>
        }
        setDatePeriod={setDatePeriodCallback}
        initDatePeriod={initDatePeriod}
        datePeriod={datePeriod}
      >
        <div className={styles.EventsView_Header} ref={headerRef}>
          <div className={styles.EventsView_Header_Filter}>
            <Tooltip title={eventTags.length === 0 ? t('There are no events to filter') : ''} arrow>
              <div className={widgetConstants.EVENTS_WIDGET_5}>
                {loadingEventsStatus === 'pending' ? (
                  <div className="ml-8">
                    <LoadingPlaceholderContainer height={FILTER_BUTTON_HEIGHT} width={FILTER_BUTTON_WIDTH} />
                  </div>
                ) : (
                  <ToggleButton
                    isCollapsed={filterIsCollapsed}
                    onClick={toggleFilterCollapse}
                    disabled={eventTags.length === 0}
                  >
                    <span>
                      {t('Filter')}
                      {filterIcon}
                    </span>
                  </ToggleButton>
                )}
              </div>
            </Tooltip>
            <Tooltip
              title={t(
                'You only see your personal drafts as default. With this on you will also see drafts created by other users.'
              )}
              arrow
              placement="right"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOtherUsersDrafts}
                    onChange={() => {
                      setShowOtherUsersDrafts(!showOtherUsersDrafts)
                    }}
                    value="showOtherUsersDrafts"
                  />
                }
                className={styles.EventsView_PersonalDraftsCheckbox}
                labelPlacement="start"
                label={t(`See other users drafts`)}
              />
            </Tooltip>
          </div>
          <div className={widgetConstants.EVENTS_WIDGET_2}>
            <Button
              primary
              marginRight
              icon="fal fa-plus"
              disabled={!hasCreateOperationalEventsAccess}
              onClick={() => {
                setCreateEventModalOpen(true)
              }}
              data-testid="new-event-button" // e2e: new event button — do not remove
            >
              {t('New event')}
            </Button>
          </div>
        </div>
      </EventsViewHeader>
      <SystemMessageBanner module="events" />
      {loadingEventsStatus !== 'pending' &&
      filteredEvents.length === 0 &&
      eventsVisibleToUser.length !== 0 &&
      filterTags.length !== allEventTagsData?.length ? (
        <ResetFilters onClick={() => resetFilters()} />
      ) : (
        <div className={styles.EventsView_Container}>
          <Box
            sx={{
              width: !isMenuOpen
                ? '100%'
                : globalSnap.navigationIsOpen
                  ? `${CONTENT_WIDTH_MENU_OPEN_IN_PERCENT}%`
                  : `${CONTENT_WIDTH_IN_PERCENT}%`,
            }}
          >
            <Card title={t('Events - Calendar')} tourId={widgetConstants.EVENTS_WIDGET_3} collapsible rememberCollapsed>
              {loadingEventsStatus === 'pending' ? (
                <LoadingPlaceholderContainer />
              ) : (
                <EventsCalendar events={filteredEvents} datePeriod={datePeriod} />
              )}
            </Card>

            <Card title={t('Events - Table')} tourId={widgetConstants.EVENTS_WIDGET_4} collapsible rememberCollapsed>
              {loadingEventsStatus === 'pending' ? (
                <LoadingPlaceholderContainer />
              ) : (
                <EventsTable events={filteredEvents} />
              )}
            </Card>

            <Tour
              isSingle={!!runTour}
              run={runTour !== null}
              setRunState={setRunTourCallback}
              steps={eventsSteps}
              dialogWidth={900}
            />
          </Box>
          {CHANGE_REQUEST_PERMISSION && (
            <ToggleMenu
              title={t('Change requests')}
              icon="fal fa-calendar-week"
              number={pendingChangeRequests?.length ?? 0}
              menuWidth={MENU_WIDTH_IN_PERCENT}
              headerHeight={headerHeight}
              tooltipLabel={t('{{count}} pending change request', {
                count: pendingChangeRequests?.length,
              })}
              onToggle={handleMenuToggle}
            >
              {(searchTerm) => {
                const searchFilteredData = pendingChangeRequests?.filter((item) =>
                  item.name?.toLowerCase().includes(searchTerm.toLowerCase())
                )

                const tagFilteredData =
                  isChangeRequestFilterActive && filterTags.length > 0
                    ? searchFilteredData?.filter((item) => {
                        const fetchEvent = events.find((event) => event.id === item.id)

                        if (fetchEvent) {
                          const matches = allowsPartialTagMatching(fetchEvent.tags ?? [], filterTags)
                          return matches
                        }

                        return false
                      })
                    : searchFilteredData

                const hiddenCount =
                  searchFilteredData && tagFilteredData ? searchFilteredData.length - tagFilteredData.length : 0

                if (tagFilteredData?.length === 0 && hiddenCount === 0) {
                  return <div className="m-4">{t('No results found')}</div>
                }

                return (
                  <div className="flex flex-col gap-2">
                    {isActiveTagsFiltered &&
                      filterTags.length > 0 &&
                      !(isChangeRequestFilterActive && hiddenCount === 0) && (
                        <div className="flex items-center">
                          <TooltipIconButton
                            icon={isChangeRequestFilterActive ? 'fas fa-filter' : 'fal fa-filter'}
                            iconSize="small"
                            onClick={toggleChangeRequestFilter}
                            iconColor={colors.grey4}
                            tooltip={
                              isChangeRequestFilterActive && hiddenCount > 0
                                ? t('Deactivate filtering for this menu to see all change requests')
                                : t('Activate filtering for this menu')
                            }
                          />
                          {isChangeRequestFilterActive && hiddenCount > 0 ? (
                            <span className="text-xs text-grey-3 ml-2">
                              {t('{{count}} change request is hidden by active filtering', { count: hiddenCount })}
                            </span>
                          ) : !isChangeRequestFilterActive ? (
                            <span className="text-xs text-grey-3 ml-2">{t('Filtering is inactive for this menu')}</span>
                          ) : null}
                        </div>
                      )}

                    {tagFilteredData?.map((item) => (
                      <ChangeRequestMenuItem label={item.name} id={item.id} item={item} />
                    ))}
                  </div>
                )
              }}
            </ToggleMenu>
          )}
        </div>
      )}
      {createEventModalOpen && <EventPostModal newEvent closeModal={() => setCreateEventModalOpen(false)} />}
      {openEventPostModal && (
        <EventPostModal
          selectedEventPostId={pathNameId}
          closeModal={() => {
            setOpenEventPostModal(false)
            resetPathName()
          }}
        />
      )}
      {openSelectedEventPostViewModal && (
        <EventPostViewModal
          selectedEventPostId={pathNameId}
          closeModal={() => {
            setOpenSelectedEventPostViewModal(false)
            resetPathName()
          }}
        />
      )}
    </div>
  )
}
